import { toast } from 'react-toastify';

import { getSerializedErrorMessage } from '../../../../../utils/errors';
import {
  mergeOnboardingQuestionnaireDocumentsPreservingUrls,
  upsertOnboardingQuestionnaireDocument,
} from './utils';

import type { ActionReducerMapBuilder, PayloadAction } from '@reduxjs/toolkit';
import type { IOnboardingQuestionnaireDocument } from '../../../../../pages/questionnaires/onboarding/types';
import type {
  GetOnboardingQuestionnaireByIdPayload,
  OnboardingQuestionnaireState,
} from '../../types';
import type {
  DeleteOnboardingQuestionnaireDocumentPayload,
  OnboardingDocumentsThunks,
  UploadOnboardingQuestionnaireDocumentPayload,
} from './types';

export function addOnboardingDocumentsCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingDocumentsThunks,
) {
  addUploadOnboardingQuestionnaireDocumentCases(builder, thunks);
  addGetOnboardingQuestionnaireDocumentDetailsCases(builder, thunks);
  addGetOnboardingQuestionnaireDocumentsCases(builder, thunks);
  addDeleteOnboardingQuestionnaireDocumentCases(builder, thunks);
}

function addGetOnboardingQuestionnaireDocumentsCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingDocumentsThunks,
) {
  const { getOnboardingQuestionnaireDocuments } = thunks;
  builder
    .addCase(getOnboardingQuestionnaireDocuments.pending, (state: OnboardingQuestionnaireState) => {
      state.error = null;
    })
    .addCase(
      getOnboardingQuestionnaireDocuments.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<
          IOnboardingQuestionnaireDocument[],
          string,
          { arg: GetOnboardingQuestionnaireByIdPayload }
        >,
      ) => {
        if (
          !state.onboardingQuestionnaireDetails ||
          String(state.onboardingQuestionnaireDetails.id) !==
            action.meta.arg.onboardingQuestionnaireId
        ) {
          return;
        }
        state.onboardingQuestionnaireDetails.documents =
          mergeOnboardingQuestionnaireDocumentsPreservingUrls(
            state.onboardingQuestionnaireDetails.documents,
            action.payload,
          );
      },
    )
    .addCase(
      getOnboardingQuestionnaireDocuments.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
      },
    );
}

function addDeleteOnboardingQuestionnaireDocumentCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingDocumentsThunks,
) {
  const { deleteOnboardingQuestionnaireDocument } = thunks;
  builder
    .addCase(
      deleteOnboardingQuestionnaireDocument.pending,
      (state: OnboardingQuestionnaireState) => {
        state.deleteDocumentLoading = true;
        state.error = null;
      },
    )
    .addCase(
      deleteOnboardingQuestionnaireDocument.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<void, string, { arg: DeleteOnboardingQuestionnaireDocumentPayload }>,
      ) => {
        const { documentId, onboardingQuestionnaireId } = action.meta.arg;
        state.deleteDocumentLoading = false;
        if (
          !state.onboardingQuestionnaireDetails ||
          String(state.onboardingQuestionnaireDetails.id) !== onboardingQuestionnaireId
        ) {
          return;
        }
        state.onboardingQuestionnaireDetails.documents =
          state.onboardingQuestionnaireDetails.documents.filter(
            (document) => String(document.id) !== documentId,
          );
      },
    )
    .addCase(
      deleteOnboardingQuestionnaireDocument.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.deleteDocumentLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}

function addGetOnboardingQuestionnaireDocumentDetailsCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingDocumentsThunks,
) {
  const { getOnboardingQuestionnaireDocumentDetails } = thunks;

  builder
    .addCase(
      getOnboardingQuestionnaireDocumentDetails.pending,
      (state: OnboardingQuestionnaireState) => {
        state.pendingDocumentDetailsLoadingCount += 1;
        state.documentDetailsLoading = state.pendingDocumentDetailsLoadingCount > 0;
        state.error = null;
      },
    )
    .addCase(
      getOnboardingQuestionnaireDocumentDetails.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<IOnboardingQuestionnaireDocument>,
      ) => {
        state.pendingDocumentDetailsLoadingCount = Math.max(
          state.pendingDocumentDetailsLoadingCount - 1,
          0,
        );
        state.documentDetailsLoading = state.pendingDocumentDetailsLoadingCount > 0;
        upsertOnboardingQuestionnaireDocument(state, action.payload);
      },
    )
    .addCase(
      getOnboardingQuestionnaireDocumentDetails.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.pendingDocumentDetailsLoadingCount = Math.max(
          state.pendingDocumentDetailsLoadingCount - 1,
          0,
        );
        state.documentDetailsLoading = state.pendingDocumentDetailsLoadingCount > 0;
        // No toast for aborted requests — the page that asked for the document is gone.
        if (!action.meta.aborted) {
          toast.error(getSerializedErrorMessage(action.error));
        }
      },
    );
}

function addUploadOnboardingQuestionnaireDocumentCases(
  builder: ActionReducerMapBuilder<OnboardingQuestionnaireState>,
  thunks: OnboardingDocumentsThunks,
) {
  const { uploadOnboardingQuestionnaireDocument } = thunks;

  builder
    .addCase(
      uploadOnboardingQuestionnaireDocument.pending,
      (state: OnboardingQuestionnaireState) => {
        state.uploadDocumentLoading = true;
        state.error = null;
      },
    )
    .addCase(
      uploadOnboardingQuestionnaireDocument.fulfilled,
      (
        state: OnboardingQuestionnaireState,
        action: PayloadAction<
          IOnboardingQuestionnaireDocument[],
          string,
          { arg: UploadOnboardingQuestionnaireDocumentPayload }
        >,
      ) => {
        state.uploadDocumentLoading = false;
        if (
          !state.onboardingQuestionnaireDetails ||
          String(state.onboardingQuestionnaireDetails.id) !==
            action.meta.arg.onboardingQuestionnaireId
        ) {
          return;
        }
        state.onboardingQuestionnaireDetails.documents =
          mergeOnboardingQuestionnaireDocumentsPreservingUrls(
            state.onboardingQuestionnaireDetails.documents,
            action.payload,
          );
      },
    )
    .addCase(
      uploadOnboardingQuestionnaireDocument.rejected,
      (state: OnboardingQuestionnaireState, action) => {
        state.error = action.error;
        state.uploadDocumentLoading = false;
        toast.error(getSerializedErrorMessage(action.error));
      },
    );
}
